<template>
  <div class="card-wrap">
    <h3 class="title">Vista previa</h3>
    <p class="sub">
      Revisa los primeros registros antes de importar. Estos son los primeros
      {{ previewResults.length }} de {{ totalRows }} registros.
    </p>

    <div class="stats-grid">
      <div class="stat-card">
        <div class="stat-num primary">{{ totalRows }}</div>
        <div class="stat-label">Registros totales</div>
      </div>
      <div class="stat-card">
        <div class="stat-num teal">{{ okCount }}</div>
        <div class="stat-label">Listos en muestra</div>
      </div>
      <div class="stat-card">
        <div class="stat-num orange">0</div>
        <div class="stat-label">Advertencias</div>
      </div>
      <div class="stat-card">
        <div class="stat-num red">{{ errorCount }}</div>
        <div class="stat-label">Errores en muestra</div>
      </div>
    </div>

    <div v-if="errorCount > 0" class="err-banner">
      <i class="el-icon-circle-close"></i>
      <div>
        <strong>{{ errorCount }} fila(s) con error en la muestra.</strong>
        Activa "Saltar filas con error" abajo para ignorarlas e importar el
        resto, o vuelve atrás a corregir el mapeo.
      </div>
    </div>

    <div class="table-scroll">
      <table class="preview-table">
        <thead>
          <tr>
            <th>#</th>
            <th v-for="c in headerCols" :key="c">{{ c }}</th>
            <th>Estado</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(r, i) in previewResults"
            :key="i"
            :class="r.status === 'error' ? 'row-err' : 'row-ok'"
          >
            <td class="row-idx">{{ r.row }}</td>
            <td v-for="c in headerCols" :key="c">
              {{ truncate(valueOf(r.data, c)) }}
            </td>
            <td>
              <el-tooltip
                v-if="r.status === 'error'"
                :content="(r.errors || []).join(' · ')"
                placement="left"
              >
                <span class="status-pill err">
                  <span class="dot"></span> Error
                </span>
              </el-tooltip>
              <span v-else class="status-pill ok">
                <span class="dot"></span> OK
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="options">
      <div class="options-title">Opciones de importación</div>

      <div class="option-row">
        <div>
          <div class="option-label">Saltar filas con error</div>
          <div class="option-desc">
            Importa los registros válidos e ignora los que tienen errores.
          </div>
        </div>
        <el-switch
          :value="options.skip_errors"
          @change="updateOption('skip_errors', $event)"
          active-color="#3ecfb4"
        />
      </div>

      <div class="option-row">
        <div>
          <div class="option-label">Actualizar registros existentes</div>
          <div class="option-desc">
            Si un registro ya existe (por código/RUC/N° doc), actualiza sus
            datos en lugar de duplicar.
          </div>
        </div>
        <el-switch
          :value="options.update_existing"
          @change="updateOption('update_existing', $event)"
          active-color="#3ecfb4"
        />
      </div>

      <div v-if="module === 'invoices'" class="option-row">
        <div>
          <div class="option-label">Crear clientes automáticamente</div>
          <div class="option-desc">
            Si el RUC de la factura no existe en el directorio, crea el cliente
            con datos básicos.
          </div>
        </div>
        <el-switch
          :value="options.create_missing_clients"
          @change="updateOption('create_missing_clients', $event)"
          active-color="#3ecfb4"
        />
      </div>
    </div>

    <div class="btn-row">
      <el-button plain @click="$emit('back')">← Atrás</el-button>
      <el-button type="success" @click="$emit('start')">
        Importar ahora →
      </el-button>
    </div>
  </div>
</template>

<script>
export default {
  name: "StepPreview",
  props: {
    module: { type: String, required: true },
    sessionId: { type: [Number, String], default: null },
    previewResults: { type: Array, default: () => [] },
    totalRows: { type: Number, default: 0 },
    errorCount: { type: Number, default: 0 },
    okCount: { type: Number, default: 0 },
    options: { type: Object, default: () => ({}) }
  },
  computed: {
    headerCols() {
      const first = this.previewResults[0];
      if (!first || !first.data) return [];
      return Object.keys(first.data).slice(0, 6);
    }
  },
  methods: {
    valueOf(row, key) {
      if (!row) return "";
      const v = row[key];
      return v === null || v === undefined ? "" : String(v);
    },
    truncate(s) {
      if (!s) return "";
      return s.length > 30 ? s.slice(0, 30) + "…" : s;
    },
    updateOption(key, value) {
      const next = { ...this.options, [key]: value };
      this.$emit("update:options", next);
    }
  }
};
</script>

<style lang="scss" scoped>
.card-wrap {
  background: #fff;
  border: 0.5px solid #e4eaf6;
  border-radius: 12px;
  padding: 1.75rem;
}

.title {
  font-size: 17px;
  font-weight: 600;
  color: #2d2b55;
  margin: 0 0 4px;
}

.sub {
  font-size: 13px;
  color: #6e7484;
  margin: 0 0 1.25rem;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 1.25rem;

  @media (max-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
  }
}

.stat-card {
  background: #f7f7f5;
  border-radius: 8px;
  padding: 1rem;
}

.stat-num {
  font-size: 22px;
  font-weight: 700;
  margin-bottom: 2px;

  &.primary {
    color: #2d2b55;
  }
  &.teal {
    color: #3ecfb4;
  }
  &.orange {
    color: #ef9f27;
  }
  &.red {
    color: #e24b4a;
  }
}

.stat-label {
  font-size: 12px;
  color: #6e7484;
}

.err-banner {
  display: flex;
  gap: 10px;
  padding: 12px 14px;
  background: #fcebeb;
  border-left: 3px solid #e24b4a;
  border-radius: 8px;
  font-size: 12px;
  color: #501313;
  margin-bottom: 1rem;

  i {
    font-size: 16px;
    margin-top: 1px;
  }
}

.table-scroll {
  overflow-x: auto;
  border: 0.5px solid #e4eaf6;
  border-radius: 8px;
}

.preview-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  background: #fff;

  th {
    background: #2d2b55;
    color: #fff;
    padding: 8px 12px;
    font-weight: 500;
    font-size: 11px;
    text-align: left;
    white-space: nowrap;
  }

  td {
    padding: 8px 12px;
    border-bottom: 0.5px solid #e4eaf6;
    color: #2d2b55;
    white-space: nowrap;
  }

  tr:last-child td {
    border-bottom: none;
  }

  tr.row-err td {
    background: #fdf3f3;
  }

  .row-idx {
    color: #6e7484;
    font-size: 11px;
    width: 30px;
  }
}

.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 20px;

  .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    display: inline-block;
  }

  &.ok {
    background: #e1f5ee;
    color: #0f6e56;
    .dot {
      background: #3ecfb4;
    }
  }

  &.err {
    background: #fcebeb;
    color: #501313;
    .dot {
      background: #e24b4a;
    }
  }
}

.options {
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 0.5px solid #e4eaf6;
}

.options-title {
  font-size: 13px;
  font-weight: 600;
  color: #2d2b55;
  margin-bottom: 8px;
}

.option-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 0.5px solid #e4eaf6;
  gap: 14px;

  &:last-child {
    border-bottom: none;
  }
}

.option-label {
  font-size: 13px;
  font-weight: 600;
  color: #2d2b55;
}

.option-desc {
  font-size: 12px;
  color: #6e7484;
  margin-top: 2px;
}

.btn-row {
  display: flex;
  justify-content: space-between;
  margin-top: 1.5rem;
}
</style>
